import React, { useEffect, useMemo, useState } from "react";
import { HELP_HOME_URL, HelpTopic } from "../utils/helpTopics";
import { ensureHelpSession } from "../utils/helpSession";
import { LEGAL_CONTACT_EMAIL } from "../legal/legalVersions";
import useAuthToken from "../hooks/useAuthToken";
import styles from "./HelpDrawer.module.css";

interface HelpDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  topic: HelpTopic | null;
}

const commonTopics = [
  { title: "Setup checklist", url: `${HELP_HOME_URL}docs/getting-started/setup-checklist` },
  { title: "Understand roles and access", url: `${HELP_HOME_URL}docs/getting-started/understand-roles-and-access` },
  { title: "I cannot see a page", url: `${HELP_HOME_URL}docs/troubleshooting/i-cannot-see-a-page` },
  { title: "My report is missing data", url: `${HELP_HOME_URL}docs/troubleshooting/my-report-is-missing-data` },
];

const HelpDrawer: React.FC<HelpDrawerProps> = ({ isOpen, onClose, topic }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [helpError, setHelpError] = useState("");
  const [openingUrl, setOpeningUrl] = useState("");
  const { getToken } = useAuthToken();

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setHelpError("");
      setOpeningUrl("");
    }
  }, [isOpen]);

  const openHelpUrl = async (event: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    event.preventDefault();
    setHelpError("");
    setOpeningUrl(url);
    try {
      await ensureHelpSession(getToken);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      setHelpError(error instanceof Error ? error.message : "Unable to open help.");
    } finally {
      setOpeningUrl("");
    }
  };

  const relatedTopics = useMemo(() => {
    const candidates = topic?.related?.length ? topic.related : commonTopics;
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return candidates;

    return candidates.filter((item) => item.title.toLowerCase().includes(normalized));
  }, [searchTerm, topic?.related]);

  if (!isOpen) return null;

  const primaryTitle = topic?.title ?? "LandAgility Help";
  const primaryDescription = topic?.description ?? "Browse help articles for LandAgility workflows.";
  const primaryUrl = topic?.url ?? HELP_HOME_URL;

  return (
    <>
      <button
        type="button"
        className={styles["help-drawer-backdrop"]}
        onClick={onClose}
        aria-label="Close help"
      />
      <aside
        className={styles["help-drawer-panel"]}
        role="dialog"
        aria-modal="true"
        aria-labelledby="helpDrawerTitle"
      >
        <div className={styles["help-drawer-header"]}>
          <div className={styles["help-drawer-heading"]}>
            <p className={styles["help-drawer-kicker"]}>Help</p>
            <h2 id="helpDrawerTitle" className={styles["help-drawer-title"]}>
              Help for this page
            </h2>
          </div>
          <button
            type="button"
            className={styles["help-drawer-close"]}
            onClick={onClose}
            aria-label="Close help drawer"
          >
            ×
          </button>
        </div>

        <div className={styles["help-drawer-body"]}>
          <section className={styles["help-drawer-card"]}>
            <h3 className={styles["help-drawer-card-title"]}>{primaryTitle}</h3>
            <p className={styles["help-drawer-description"]}>{primaryDescription}</p>
            <a
              className={styles["help-drawer-primary"]}
              href={primaryUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => openHelpUrl(event, primaryUrl)}
            >
              {openingUrl === primaryUrl ? "Opening..." : "Open article"}
            </a>
            {helpError && <p className={styles["help-drawer-error"]}>{helpError}</p>}
          </section>

          <section className={styles["help-drawer-card"]}>
            <h3 className={styles["help-drawer-card-title"]}>Find related help</h3>
            <input
              className={styles["help-drawer-search"]}
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Filter suggested articles"
              aria-label="Filter suggested help articles"
            />
          </section>

          <section className={styles["help-drawer-card"]}>
            <h3 className={styles["help-drawer-card-title"]}>Suggested articles</h3>
            {relatedTopics.length > 0 ? (
              <div className={styles["help-drawer-link-list"]}>
                {relatedTopics.map((item) => (
                  <a
                    className={styles["help-drawer-link"]}
                    href={item.url}
                    key={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => openHelpUrl(event, item.url)}
                  >
                    {openingUrl === item.url ? "Opening..." : item.title}
                  </a>
                ))}
              </div>
            ) : (
              <p className={styles["help-drawer-empty"]}>No suggested articles match that search.</p>
            )}
          </section>
        </div>

        <div className={styles["help-drawer-footer"]}>
          Need more help? Contact{" "}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className={styles["help-drawer-footer-link"]}>
            {LEGAL_CONTACT_EMAIL}
          </a>
          .
        </div>
      </aside>
    </>
  );
};

export default HelpDrawer;
